/* ═══════════════════════════════════════════════════════════════════════
   pd-compare.js — comparaison d'images avant / après (Palladium).

   Va avec pd-compare.css ; balisage rendu par ImageCompareView.razor (bloc
   « Comparaison d'images » du PageBuilder, galerie produit, zoom). Le CSS
   affiche la comparaison à 50 % ; ce script n'ajoute que l'interaction.

   window.PdCompare
     .scan(scope)        lie toutes les comparaisons trouvées (idempotent)
     .bind(root)         lie une comparaison .pd-compare
     .track(surface, o)  moteur de glissé seul, réutilisé par l'ancien bloc
                         « Avant / après » du pack Interactif (pb-widgets.js)

   Geste :
     souris / stylet  appui = la séparation saute à la position, puis suit ;
     doigt            le glissé ne part qu'après un vrai mouvement horizontal
                      (touch-action: pan-y) : un balayage vertical fait défiler
                      la page sans bouger la séparation ; un toucher bref =
                      saut à la position. En vertical, seule la poignée se
                      saisit au doigt — la page défile partout ailleurs ;
     survol           (option) la séparation suit la souris, sans clic ;
     clavier          curseur natif (flèches, Page préc./suiv., Début/Fin),
                      valeur annoncée aux lecteurs d'écran.
   Après un glissé, le clic qui suit est absorbé (la galerie n'ouvre pas son
   zoom sous le doigt). Idempotent (data-pdc-live) et re-scanné via
   MutationObserver : rendus Blazor, îlots et navigations améliorées couverts.
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';

    if (window.PdCompare) return;

    var reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var DRAG_THRESHOLD = 4;     // px avant qu'un toucher devienne un glissé
    var EDGE = 12;              // % sous lequel le libellé du côté presque caché s'efface

    function clamp(v) {
        v = typeof v === 'number' ? v : parseFloat(v);
        if (!isFinite(v)) return 50;
        return Math.max(0, Math.min(100, v));
    }

    function round(v) { return Math.round(v * 10) / 10; }

    /** Absorbe le prochain clic dans `el` (fin de glissé : ce n'était pas un clic). */
    function swallowNextClick(el) {
        function stop(e) {
            if (el.contains(e.target)) { e.preventDefault(); e.stopPropagation(); }
            window.removeEventListener('click', stop, true);
        }
        window.addEventListener('click', stop, true);
        setTimeout(function () { window.removeEventListener('click', stop, true); }, 400);
    }

    /**
     * Moteur de glissé générique.
     *   o.vertical  séparation horizontale (avant en haut)
     *   o.hover     la séparation suit la souris sans clic
     *   o.handle    élément saisissable au doigt en vertical (la poignée)
     *   o.set(pct, animate)  applique une position 0–100
     *   o.onStart / o.onEnd  début / fin de glissé (classes d'état)
     */
    function track(surface, o) {
        o = o || {};
        var dragging = false, active = false, pid = null, sx = 0, sy = 0, moved = false, touch = false;
        var raf = 0, pending = null;

        function pctFrom(e) {
            var r = surface.getBoundingClientRect();
            var span = o.vertical ? r.height : r.width;
            if (!span) return null;
            var off = o.vertical ? e.clientY - r.top : e.clientX - r.left;
            return clamp(off / span * 100);
        }

        function apply(p, animate) {
            if (p === null) return;
            pending = [p, !!animate];
            if (raf) return;
            raf = requestAnimationFrame(function () {
                raf = 0;
                var x = pending; pending = null;
                if (x) o.set(x[0], x[1]);
            });
        }

        function begin() {
            if (active) return;
            active = true;
            try { surface.setPointerCapture(pid); } catch (err) { /* pointeur déjà relâché */ }
            if (o.onStart) o.onStart();
        }

        function finish() {
            var was = active;
            dragging = false; active = false; pid = null;
            if (was && o.onEnd) o.onEnd();
            if (was && moved) swallowNextClick(surface);
        }

        surface.addEventListener('pointerdown', function (e) {
            if (e.button > 0) return;
            touch = e.pointerType === 'touch';
            if (o.vertical && touch && !(o.handle && o.handle.contains(e.target))) return;
            dragging = true; moved = false; pid = e.pointerId; sx = e.clientX; sy = e.clientY;
            if (!touch) {
                apply(pctFrom(e), true);
                begin();
                e.preventDefault();        // pas de sélection de texte ni de glisser-déposer d'image
            }
        });

        surface.addEventListener('pointermove', function (e) {
            if (o.hover && e.pointerType === 'mouse' && !dragging) { apply(pctFrom(e), false); return; }
            if (!dragging || e.pointerId !== pid) return;
            var dx = Math.abs(e.clientX - sx), dy = Math.abs(e.clientY - sy);
            if (!active) {
                if (Math.max(dx, dy) < DRAG_THRESHOLD) return;
                // Au doigt, en horizontal : un geste surtout vertical appartient à la page.
                if (touch && !o.vertical && dy > dx) { dragging = false; return; }
                begin();
            }
            if (dx + dy > 3) moved = true;
            e.preventDefault();
            apply(pctFrom(e), false);
        });

        function up(e) {
            if (!dragging || e.pointerId !== pid) return;
            // Toucher bref sans glissé : saut à la position touchée.
            if (touch && !active && !(o.vertical)) apply(pctFrom(e), true);
            finish();
        }
        surface.addEventListener('pointerup', up);
        surface.addEventListener('pointercancel', function (e) { if (e.pointerId === pid) finish(); });
        // Seule la capture du CADRE compte : au doigt, l'enfant touché (poignée, image) détient la capture
        // implicite et la perd quand le cadre la prend — cet événement-là remonte et ne termine rien.
        surface.addEventListener('lostpointercapture', function (e) { if (active && e.pointerId === pid && e.target === surface) finish(); });
        // Le navigateur ne doit pas lancer son glisser-déposer d'image natif.
        surface.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }

    function bind(root) {
        if (!root || root.getAttribute('data-pdc-live') === '1') return;
        root.setAttribute('data-pdc-live', '1');
        if (root.classList.contains('pd-compare--single')) return;

        var vertical = root.classList.contains('pd-compare--v');
        var hover = root.classList.contains('pd-compare--hover');
        var range = root.querySelector('.pd-compare-range');
        var knob = root.querySelector('.pd-compare-knob');
        var before = root.querySelector('.pd-compare-before');
        var valueText = root.getAttribute('data-pdc-valuetext') || '{before} % / {after} %';
        var animTimer = 0;

        // Dans un carrousel Swiper, glisser la séparation ne doit pas changer de diapositive.
        if (root.closest && root.closest('.swiper')) root.classList.add('swiper-no-swiping');

        function set(p, animate) {
            p = clamp(p);
            if (animate && !reducedMotion) {
                root.classList.add('pd-compare--animate');
                clearTimeout(animTimer);
                animTimer = setTimeout(function () { root.classList.remove('pd-compare--animate'); }, 260);
            } else {
                root.classList.remove('pd-compare--animate');
            }
            var r = round(p);
            root.style.setProperty('--pdc-pos', r + '%');
            root.setAttribute('data-pdc-edge', r < EDGE ? 'start' : r > 100 - EDGE ? 'end' : '');
            if (range) {
                if (document.activeElement !== range || String(range.value) !== String(Math.round(r))) range.value = String(Math.round(r));
                range.setAttribute('aria-valuetext', valueText
                    .replace('{before}', String(Math.round(r)))
                    .replace('{after}', String(Math.round(100 - r))));
            }
        }

        set(root.getAttribute('data-pdc-start') || (range && range.value) || 50, false);

        track(root, {
            vertical: vertical,
            hover: hover,
            handle: knob,
            set: set,
            onStart: function () { root.classList.add('pd-compare--dragging'); },
            onEnd: function () { root.classList.remove('pd-compare--dragging'); },
        });

        if (range) {
            range.addEventListener('input', function () { set(range.value, true); });
            range.addEventListener('focus', function () { root.classList.add('pd-compare--focus'); });
            range.addEventListener('blur', function () { root.classList.remove('pd-compare--focus'); });
            // En vertical, « flèche bas » descend la séparation (le curseur natif ferait l'inverse).
            if (vertical) {
                range.addEventListener('keydown', function (e) {
                    var v = clamp(range.value), step = e.shiftKey ? 10 : 1, next = null;
                    switch (e.key) {
                        case 'ArrowDown': case 'ArrowRight': next = v + step; break;
                        case 'ArrowUp': case 'ArrowLeft': next = v - step; break;
                        case 'PageDown': next = v + 10; break;
                        case 'PageUp': next = v - 10; break;
                        case 'Home': next = 0; break;
                        case 'End': next = 100; break;
                    }
                    if (next === null) return;
                    e.preventDefault();
                    set(next, true);
                });
            }
        }

        // Proportions « automatiques » non mesurées à l'avance (mode dynamique) : celles de l'image avant.
        if (root.getAttribute('data-pdc-ratio') === 'auto' && before) {
            var fit = function () {
                if (before.naturalWidth > 0 && before.naturalHeight > 0) {
                    root.style.aspectRatio = before.naturalWidth + ' / ' + before.naturalHeight;
                    // Rapport numérique : un conteneur peut borner la largeur sur la hauteur (zoom plein écran).
                    root.style.setProperty('--pdc-r', String(before.naturalWidth / before.naturalHeight));
                }
            };
            if (before.complete) fit(); else before.addEventListener('load', fit, { once: true });
        }
    }

    function scan(scope) {
        if (!scope || !scope.querySelectorAll) return;
        if (scope.matches && scope.matches('.pd-compare')) bind(scope);
        var list = scope.querySelectorAll('.pd-compare');
        for (var i = 0; i < list.length; i++) bind(list[i]);
    }

    window.PdCompare = { scan: scan, bind: bind, track: track };

    function start() {
        scan(document);
        if (!window.MutationObserver || !document.body) return;
        new MutationObserver(function (muts) {
            for (var i = 0; i < muts.length; i++) {
                var added = muts[i].addedNodes;
                for (var j = 0; j < added.length; j++) if (added[j].nodeType === 1) scan(added[j]);
            }
        }).observe(document.body, { childList: true, subtree: true });
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
